import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  createCirugia,
  getHospitales,
  getTiposCirugia,
  updateCirugia,
  Cirugia,
  CreateCirugiaDto,
} from '../../services/cirugiasService';
import SearchableSelect from '../common/SearchableSelect';
import api from '../../services/api';

interface Props {
  onClose: () => void;
  onSuccess: () => void;
  pacienteId?: number;
  cirugia?: Cirugia;
}

export default function FormCirugiaModal({ onClose, onSuccess, pacienteId, cirugia }: Props) {
  const [loading, setLoading] = useState(false);
  const [pacientes, setPacientes] = useState<any[]>([]);
  const [hospitales, setHospitales] = useState<any[]>([]);
  const [tiposCirugia, setTiposCirugia] = useState<any[]>([]);
  const isEdit = Boolean(cirugia);

  const [formData, setFormData] = useState<CreateCirugiaDto>({
    pacienteId: pacienteId || 0,
    hospitalId: 0,
    tipoCirugiaId: 0,
    fechaProgramada: '',
    horaInicio: '',
    padecimiento: '',
    salaQuirurgica: '',
    estado: 'prospecto',
    tipoPago: 'sin_seguro',
    datosSeguro: '',
    diagnosticoPreoperatorio: '',
    notasPreoperatorias: '',
    observaciones: '',
    tipoAnestesia: '',
    riesgoQuirurgico: 'medio',
    requiereUci: false,
    diasHospitalizacion: undefined,
    numeroExpedienteHospital: '',
  });

  useEffect(() => {
    loadCatalogos();
  }, []);

  useEffect(() => {
    if (cirugia) {
      setFormData({
        pacienteId: cirugia.pacienteId || 0,
        hospitalId: cirugia.hospitalId || 0,
        tipoCirugiaId: cirugia.tipoCirugiaId || 0,
        fechaProgramada: cirugia.fechaProgramada
          ? new Date(cirugia.fechaProgramada).toISOString().split('T')[0]
          : '',
        horaInicio: cirugia.horaInicio || '',
        padecimiento: cirugia.padecimiento || '',
        salaQuirurgica: cirugia.salaQuirurgica || '',
        estado: cirugia.estado || 'prospecto',
        tipoPago: cirugia.tipoPago || 'sin_seguro',
        datosSeguro: cirugia.datosSeguro || '',
        diagnosticoPreoperatorio: cirugia.diagnosticoPreoperatorio || '',
        notasPreoperatorias: cirugia.notasPreoperatorias || '',
        observaciones: cirugia.observaciones || '',
        tipoAnestesia: cirugia.tipoAnestesia || '',
        riesgoQuirurgico: cirugia.riesgoQuirurgico || 'medio',
        requiereUci: cirugia.requiereUci ?? false,
        diasHospitalizacion: cirugia.diasHospitalizacion,
        numeroExpedienteHospital: cirugia.numeroExpedienteHospital || '',
      });
    }
  }, [cirugia]);

  const loadCatalogos = async () => {
    try {
      const [pacientesRes, hospitalesData, tiposData] = await Promise.all([
        api.get('/medico-vfp/pacientes?take=500'),
        getHospitales({ isActive: true, take: 500 }),
        getTiposCirugia({ isActive: true, take: 500 }),
      ]);
      setPacientes(pacientesRes.data?.data?.data || pacientesRes.data?.data || []);
      setHospitales(hospitalesData.data?.data || hospitalesData.data || []);
      setTiposCirugia(tiposData.data?.data || tiposData.data || []);
    } catch (error) {
      console.error('Error al cargar catálogos:', error);
      toast.error('Error al cargar catálogos');
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData((prev) => {
      let parsedValue: any;
      if (type === 'checkbox') {
        parsedValue = (e.target as HTMLInputElement).checked;
      } else if (type === 'number') {
        parsedValue = value === '' ? undefined : parseInt(value);
      } else {
        parsedValue = value;
      }

      const updated = {
        ...prev,
        [name]: parsedValue,
      } as CreateCirugiaDto;

      if (name === 'tipoPago' && value === 'sin_seguro') {
        updated.datosSeguro = '';
      }

      return updated;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.pacienteId) {
      toast.error('Debe seleccionar un paciente');
      return;
    }

    const payload: any = {
      ...formData,
      fechaProgramada: formData.fechaProgramada || undefined,
      horaInicio: formData.horaInicio || undefined,
      salaQuirurgica: formData.salaQuirurgica || undefined,
      numeroExpedienteHospital: formData.numeroExpedienteHospital || undefined,
      tipoPago: formData.tipoPago,
      datosSeguro: formData.tipoPago === 'seguro' ? formData.datosSeguro || '' : '',
      diagnosticoPreoperatorio: formData.diagnosticoPreoperatorio || undefined,
      notasPreoperatorias: formData.notasPreoperatorias || undefined,
      observaciones: formData.observaciones || undefined,
      tipoAnestesia: formData.tipoAnestesia || undefined,
      riesgoQuirurgico: formData.riesgoQuirurgico || undefined,
      hospitalId: formData.hospitalId || undefined,
      tipoCirugiaId: formData.tipoCirugiaId || undefined,
    };

    try {
      setLoading(true);
      if (isEdit && cirugia) {
        await updateCirugia(cirugia.id, payload);
        toast.success('Cirugía actualizada exitosamente');
      } else {
        await createCirugia(payload as CreateCirugiaDto);
        toast.success('Cirugía creada exitosamente');
      }
      onSuccess();
    } catch (error: any) {
      console.error('Error al guardar cirugía:', error);
      toast.error(error.response?.data?.message || 'Error al guardar cirugía');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">
            {isEdit ? 'Editar Cirugía' : 'Nueva Cirugía'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Información Básica */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Información Básica</h3>

            <div className="grid grid-cols-2 gap-4">
              <SearchableSelect
                label="Paciente"
                required
                value={formData.pacienteId}
                onChange={(value) => setFormData(prev => ({ ...prev, pacienteId: value }))}
                options={Array.isArray(pacientes) ? pacientes.map(p => ({
                  value: p.clave,
                  label: `${p.nombre} ${p.apellido1 || ''} ${p.apellido2 || ''}`.trim(),
                  subtitle: `Tel: ${p.medicoVfpPaciDat?.telefono || p.telefono || 'N/A'}`
                })) : []}
                placeholder="Buscar paciente por nombre..."
              />

              <SearchableSelect
                label="Hospital"
                value={formData.hospitalId}
                onChange={(value) => setFormData(prev => ({ ...prev, hospitalId: value }))}
                options={Array.isArray(hospitales) ? hospitales.map(h => ({
                  value: h.id,
                  label: h.nombre,
                  subtitle: h.direccion
                })) : []}
                placeholder="Buscar hospital..."
              />

              <SearchableSelect
                label="Tipo de Cirugía"
                value={formData.tipoCirugiaId}
                onChange={(value) => setFormData(prev => ({ ...prev, tipoCirugiaId: value }))}
                options={Array.isArray(tiposCirugia) ? tiposCirugia.map(t => ({
                  value: t.id,
                  label: t.nombre,
                  subtitle: t.categoria || t.descripcion || undefined,
                  group: t.categoria || undefined,
                })) : []}
                placeholder="Buscar tipo de cirugía..."
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fecha Programada
                </label>
                <input
                  type="date"
                  name="fechaProgramada"
                  value={formData.fechaProgramada}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Hora Inicio
                </label>
                <input
                  type="time"
                  name="horaInicio"
                  value={formData.horaInicio}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sala Quirúrgica
                </label>
                <input
                  type="text"
                  name="salaQuirurgica"
                  value={formData.salaQuirurgica}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Estado <span className="text-red-500">*</span>
                </label>
                <select
                  name="estado"
                  value={formData.estado}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="prospecto">Prospecto</option>
                  <option value="confirmada">Confirmada</option>
                  <option value="en_proceso">En Proceso</option>
                  <option value="concluida">Concluida</option>
                  <option value="cancelada">Cancelada</option>
                  <option value="pospuesta">Pospuesta</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Padecimiento
              </label>
              <input
                type="text"
                name="padecimiento"
                value={formData.padecimiento}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Información de Pago */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Información de Pago</h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo de Pago
                </label>
                <select
                  name="tipoPago"
                  value={formData.tipoPago}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="sin_seguro">Sin Seguro</option>
                  <option value="seguro">Con Seguro</option>
                </select>
              </div>

              {formData.tipoPago === 'seguro' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Datos del Seguro
                  </label>
                  <textarea
                    name="datosSeguro"
                    value={formData.datosSeguro}
                    onChange={handleChange}
                    rows={3}
                    placeholder="Aseguradora, póliza, vigencia, etc."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Información Médica */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Información Médica</h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo de Anestesia
                </label>
                <select
                  name="tipoAnestesia"
                  value={formData.tipoAnestesia}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Seleccione</option>
                  <option value="general">General</option>
                  <option value="local">Local</option>
                  <option value="regional">Regional</option>
                  <option value="sedacion">Sedación</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Riesgo Quirúrgico
                </label>
                <select
                  name="riesgoQuirurgico"
                  value={formData.riesgoQuirurgico}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="bajo">Bajo</option>
                  <option value="medio">Medio</option>
                  <option value="alto">Alto</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Días de Hospitalización
                </label>
                <input
                  type="number"
                  name="diasHospitalizacion"
                  value={formData.diasHospitalizacion || ''}
                  onChange={handleChange}
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expediente Hospital
                </label>
                <input
                  type="text"
                  name="numeroExpedienteHospital"
                  value={formData.numeroExpedienteHospital}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                name="requiereUci"
                checked={formData.requiereUci}
                onChange={handleChange}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label className="ml-2 text-sm font-medium text-gray-700">
                Requiere UCI
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Diagnóstico Preoperatorio
              </label>
              <textarea
                name="diagnosticoPreoperatorio"
                value={formData.diagnosticoPreoperatorio}
                onChange={handleChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notas Preoperatorias
              </label>
              <textarea
                name="notasPreoperatorias"
                value={formData.notasPreoperatorias}
                onChange={handleChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Observaciones
              </label>
              <textarea
                name="observaciones"
                value={formData.observaciones}
                onChange={handleChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-white border-t border-gray-200 pt-4 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {loading ? 'Guardando...' : isEdit ? 'Actualizar Cirugía' : 'Guardar Cirugía'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
